import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {ExpandableIcon} from '@/web-components';
import {BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../../constants';
import {isBillableSplitAllowed} from '../../../../shared/util/cache/TimeRegistrationSettingsUtil';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';
import {isClientUser, showRevenue as canViewRevenue, showCost as canViewCost} from '../../../../shared/util/PermissionsUtil';
import ProjectUtil from '../../../../shared/util/ProjectUtil';
import {Element, GridContainer, Header, IconWrapper, Row, TotalsBox} from './ProjectScopingTotals_styled';
import {useProjectTotals} from './ProjectScopingTotalsContext';
import {AvailabilityTile} from './totals-header-box-tiles/AvailabilityTile';
import {DatesAndRevenueTile} from './totals-header-box-tiles/DatesAndRevenueTile';
import {EstimateAndTimeTile} from './totals-header-box-tiles/EstimateAndTimeTile';
import {RemainingAndBaselineTile} from './totals-header-box-tiles/RemainingAndBaselineTile';
import {TileVariant} from './totals-header-box-tiles/TotalsTypes';
import {FixedPriceLeftTile} from './totals-header-box-tiles/FixedPriceLeftTile';
import {FixedPriceMiddleTile} from './totals-header-box-tiles/FixedPriceMiddleTile';
import {FixedPriceRightTile} from './totals-header-box-tiles/FixedPriceRightTile';
import {Tile} from './totals-header-box-tiles/Tile_styled';
import {ProjectScopingTotalsRows as TotalsRows} from './ProjectScopingTotalsRows';
import {HorizontalBar} from './ProjectScopingTotals_styled';

const DeprecatedHeaderTotalsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
	grid-gap: 24px 48px;
	padding-right: 36px;
`;

// Responsive grid specifically for T&M, Fixed Hours and T&M retainers
const HeaderTotalsGrid = styled.div`
	display: grid;

	/* 2x2 grid layout for viewport < 1443px */
	grid-template-columns: repeat(2, minmax(250px, 1fr));
	grid-gap: 28px 48px;

	/* Single row layout (up to 4 columns) for viewport >= 1443px */
	@media (min-width: 1443px) {
		grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
		grid-gap: 24px 48px;
	}
`;

const EmptyHeaderPlaceholder = styled.div`
	height: 20px;
`;

// Attempt to fit the availability tile into the secondary row of one of the other tiles instead of showing it as its own standalone tile if there is space for it.
const getAvailabilityTile = (
	showRevenue: boolean,
	showBillableSplit: boolean,
	showBaseline: boolean,
	showAvailabilityOnScoping: boolean
): TileVariant | null => {
	if (!showAvailabilityOnScoping) return null;

	// If none of the other secondary rows are shown, show as standalone column
	if (!showRevenue && !showBillableSplit && !showBaseline) return TileVariant.Availability;

	if (!showRevenue) return TileVariant.DatesAndRevenue;

	if (!showBillableSplit) return TileVariant.EstimateAndTime;

	if (!showBaseline) return TileVariant.RemainingAndBaseline;

	return TileVariant.Availability;
};

/**
 * Create the project scoping header section in a big box (Expanded)
 *
 * @returns JSX Project Scoping Totals Box Component
 */
export const ProjectScopingTotalsBox = (): JSX.Element => {
	const {totalsExpanded, toggleTotalsExpanded, totalsData, settings, projectData} = useProjectTotals();
	const {formatMessage} = useIntl();

	const hasFinancialServiceInScoping = hasFeatureFlag('use_financial_service_in_scoping');
	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	const projectTracksRevenue = ProjectUtil.projectTracksRevenue(projectData);
	const isNonBillable = !projectTracksRevenue;

	const isFixedPriceBudgetType = projectData.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	const isFixedPriceV2 =
		projectTracksRevenue &&
		(isFixedPriceBudgetType ||
			(projectData.budgetType === BUDGET_TYPE.RETAINER &&
				projectData.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE));

	const showBillableSplit = !isNonBillable && isBillableSplitAllowed();
	const showBaseline = !!settings.isBaselineProject && totalsData.baselineHours > 0;
	const showAvailabilityOnScoping = !!settings.isUsingProjectAllocation && !isClientUser();

	const showRevenue = projectTracksRevenue && canViewRevenue();
	const showCost = canViewCost();

	const availabilityTile = getAvailabilityTile(showRevenue, showBillableSplit, showBaseline, showAvailabilityOnScoping);
	const showAvailabilityTile = availabilityTile === TileVariant.DatesAndRevenue;
	const showNewHeader = hasFinancialServiceInScoping && hasFinancialCategoriesUpdate;

	return (
		<GridContainer data-userpilot="project-totals-header">
			<TotalsBox key="dates-revenue-box" data-cy="project-totals-header" noHeader={showNewHeader}>
				<Row bottomMargin={16}>
					{!showNewHeader && (
						<Element split>
							<Header>Project totals</Header>
						</Element>
					)}
					<Element alignRight>
						<IconWrapper shiftDownwards={showNewHeader}>
							<ExpandableIcon
								isOpen={totalsExpanded}
								onClick={toggleTotalsExpanded}
								cy={'collapse-project-totals'}
							/>
						</IconWrapper>
					</Element>
				</Row>
				{showNewHeader ? (
					<HeaderTotalsGrid>
						{/* Column 1 */}
						<Tile>
							<Header bold>{formatMessage({id: 'common.hours'})}</Header>
							<HorizontalBar transparent />
							{isFixedPriceV2 ? <TotalsRows.EstimatedHours /> : <TotalsRows.PlannedHours.Planned />}
							<TotalsRows.ActualHours.Actual />
							<HorizontalBar />
							<TotalsRows.RemainingWorkHours.RemainingWork />
							<TotalsRows.ProjectedTotalHours.ProjectedTotal />
						</Tile>
						{/* Column 2 */}
						{totalsData.isEstimatedInHours ? (
							isNonBillable ? (
								showBaseline ? (
									showCost && (
										<Tile>
											<Header bold>{formatMessage({id: 'common.cost'})}</Header>
											<HorizontalBar transparent />
											<TotalsRows.BaselineCost.Baseline />
											<TotalsRows.ActualCost.Actual />
											<HorizontalBar />
											<TotalsRows.RemainingWorkCost.RemainingWork />
											<TotalsRows.ProjectedTotalCost.ProjectedTotal />
										</Tile>
									)
								) : (
									showRevenue && (
										<Tile>
											<Header bold>{formatMessage({id: 'common.financials'})}</Header>
											<HorizontalBar transparent />
											{showCost && <TotalsRows.ActualCost.ActualCost />}
											<TotalsRows.ActualValueOfService />
											<HorizontalBar />
											{showCost && <TotalsRows.ProjectedTotalCost.ProjectedTotalCost />}
											<TotalsRows.ProjectedTotalValueOfService />
										</Tile>
									)
								)
							) : isFixedPriceV2 ? (
								showBaseline ? (
									<Tile>
										<Header bold>{formatMessage({id: 'baseline.baseline'})}</Header>
										<HorizontalBar transparent />
										<TotalsRows.BaselineHours.Hours />
										{showCost && (
											<>
												<TotalsRows.BaselineCost.Cost />
												<HorizontalBar />
												<TotalsRows.BaselineProfit.Profit />
												<TotalsRows.BaselineMargin.Margin />
											</>
										)}
									</Tile>
								) : (
									<Tile>
										<Header bold>{formatMessage({id: 'project_scoping.billable_recovery'})}</Header>
										<HorizontalBar transparent />
										<TotalsRows.ActualNonBillableHours />
										<TotalsRows.ActualHoursPercentageBillable />
										<HorizontalBar />
										<TotalsRows.ProjectedTotalNonBillableHours />
										<TotalsRows.ProjectedTotalHoursPercentageBillable />
									</Tile>
								)
							) : (
								/* T&M, Fixed Hours, T&M retainers */
								<Tile>
									<Header bold>{formatMessage({id: 'project_scoping.billable_recovery'})}</Header>
									<HorizontalBar transparent />
									<TotalsRows.ActualNonBillableHours />
									<TotalsRows.ActualHoursPercentageBillable />
									<HorizontalBar />
									<TotalsRows.ProjectedTotalNonBillableHours />
									<TotalsRows.ProjectedTotalHoursPercentageBillable />
								</Tile>
							)
						) : (
							<Tile>
								<Header bold>{formatMessage({id: 'sync_jira_project_modal.radio.story_points'})}</Header>
								<HorizontalBar transparent />
								<TotalsRows.EstimatedPoints.Estimated />
								<TotalsRows.CompletedPoints.Completed />
								<HorizontalBar />
								<TotalsRows.RemainingPoints.RemainingWork />
							</Tile>
						)}
						{/* Column 3 */}
						{isNonBillable ? (
							showBaseline ? (
								<Tile>
									<Header bold>{formatMessage({id: 'common.value_of_service'})}</Header>
									<HorizontalBar transparent />
									<TotalsRows.BaselineValueOfService />
									<TotalsRows.ActualValueOfService />
									<HorizontalBar />
									{showCost && <TotalsRows.RemainingWorkCost.RemainingWork />}
									<TotalsRows.ProjectedTotalValueOfService />
								</Tile>
							) : (
								<Tile>
									<Header bold>{formatMessage({id: 'common.financials'})}</Header>
									<HorizontalBar transparent />
									{showCost && <TotalsRows.ActualCost.ActualCost />}
									<TotalsRows.ActualValueOfService />
									<HorizontalBar />
									{showCost && <TotalsRows.ProjectedTotalCost.ProjectedTotalCost />}
									<TotalsRows.ProjectedTotalValueOfService />
								</Tile>
							)
						) : isFixedPriceV2 ? (
							showRevenue && (
								<Tile>
									<Header bold>{formatMessage({id: 'project_scoping.projected_total_financials'})}</Header>
									<HorizontalBar transparent />
									<TotalsRows.ProjectedTotalRevenue.Revenue />
									{showCost && <TotalsRows.ProjectedTotalCost.Cost />}
									<HorizontalBar />
									{showCost && <TotalsRows.ProjectedTotalProfit.Profit />}
									{showCost && <TotalsRows.ProjectedTotalMargin.Margin />}
								</Tile>
							)
						) : (
							showRevenue && (
								<Tile>
									<Header bold>
										{formatMessage({
											id: projectData.useBaseline
												? 'project_scoping.baseline_financials'
												: 'project_scoping.estimated_financials',
										})}
									</Header>
									<HorizontalBar transparent />
									<TotalsRows.PlannedRevenue />
									{showCost && <TotalsRows.PlannedCost.Cost />}
									<HorizontalBar />
									{showCost && <TotalsRows.PlannedProfit />}
									{showCost && <TotalsRows.PlannedMargin />}
								</Tile>
							)
						)}
						{/* Column 4 */}
						{isNonBillable
							? null
							: isFixedPriceV2
							? showRevenue && (
									<Tile>
										<EmptyHeaderPlaceholder />
										<HorizontalBar transparent />
										<TotalsRows.BillableValueOfService />
										<TotalsRows.RemainingBudget />
										<HorizontalBar />
										<TotalsRows.ValueOfService />
										<TotalsRows.OverUnderServicing />
									</Tile>
							  )
							: showRevenue && (
									<Tile>
										<Header bold>
											{formatMessage({id: 'project_scoping.projected_total_financials'})}
										</Header>
										<HorizontalBar transparent />
										<TotalsRows.ProjectedTotalRevenue.Revenue />
										{showCost && <TotalsRows.ProjectedTotalCost.Cost />}
										<HorizontalBar />
										{showCost && <TotalsRows.ProjectedTotalProfit.Profit />}
										{showCost && <TotalsRows.ProjectedTotalMargin.Margin />}
									</Tile>
							  )}
					</HeaderTotalsGrid>
				) : (
					<DeprecatedHeaderTotalsGrid>
						{hasFinancialServiceInScoping && hasFinancialCategoriesUpdate ? (
							isFixedPriceV2 ? (
								<>
									<FixedPriceLeftTile />
									<FixedPriceMiddleTile />
									<FixedPriceRightTile />
								</>
							) : isNonBillable ? (
								totalsData.isEstimatedInHours ? (
									<>
										<Tile>
											<TotalsRows.PlannedHours.PlannedHours />
											<TotalsRows.ActualHours.ActualHours />
										</Tile>
										<Tile>
											<TotalsRows.RemainingWorkHours.RemainingWorkHours />
											<TotalsRows.ProjectedTotalHours.ProjectedTotalHours />
										</Tile>
										{showCost && (
											<Tile>
												<TotalsRows.PlannedCost.PlannedCost />
												<TotalsRows.ActualCost.ActualCost />
											</Tile>
										)}
										<Tile>
											{showCost && <TotalsRows.ProjectedTotalCost.ProjectedTotalCost />}
											<TotalsRows.Progress />
										</Tile>
									</>
								) : (
									<>
										<Tile>
											<TotalsRows.ActualHours.ActualHours />
											<TotalsRows.PlannedHours.PlannedHours />
											<TotalsRows.RemainingWorkHours.RemainingWorkHours />
											<TotalsRows.ProjectedTotalHours.ProjectedTotalHours />
										</Tile>
										<Tile>
											<TotalsRows.Progress />
											<TotalsRows.EstimatedPoints.Estimated />
											<TotalsRows.CompletedPoints.Completed />
											<TotalsRows.RemainingPoints.RemainingWork />
										</Tile>
										{showCost && (
											<Tile>
												<TotalsRows.PlannedCost.PlannedCost />
												<TotalsRows.ActualCost.ActualCost />
												<TotalsRows.RemainingCost />
												<TotalsRows.ProjectedTotalCost.ProjectedTotalCost />
											</Tile>
										)}
									</>
								)
							) : null
						) : (
							<>
								<DatesAndRevenueTile
									showRevenue={showRevenue}
									isFixedPriceV2={isFixedPriceV2}
									hasFinancialServiceInScoping={hasFinancialServiceInScoping}
									showAvailabilityTile={showAvailabilityTile}
								/>
								<EstimateAndTimeTile
									showBillableSplit={showBillableSplit}
									showAvailabilityTile={showAvailabilityTile}
								/>
								<RemainingAndBaselineTile
									showRevenue={showRevenue}
									showBaseline={showBaseline}
									showAvailabilityTile={showAvailabilityTile}
								/>
								{availabilityTile === TileVariant.Availability && <AvailabilityTile />}
							</>
						)}
					</DeprecatedHeaderTotalsGrid>
				)}
			</TotalsBox>
		</GridContainer>
	);
};
